<template>
  <NuxtLink
    :to="`/mechanicy/${suggestedGarage?.garage?.citySlug}/${suggestedGarage?.garage?.nameSlug}.html`"
    class="garage-suggested"
    target="_blank"
  >
    <div class="garage-suggested__header">
      <NuxtImg
        v-if="garageAdditional?.garageImages[0]?.url"
        placeholder="/_nuxt/logoBG.webp"
        loading="lazy"
        width="90px"
        height="80px"
        class="garage-suggested__image"
        :alt="suggestedGarage?.garage?.name"
        :src="garageAdditional.garageImages[0].url"
      />

      <div>
        <h3 class="garage-suggested__name">{{ suggestedGarage?.garage?.name }}</h3>

        <div class="garage-suggested__rating">
          <div class="garage-suggested__rating-stars">
            <NuxtImg
              v-for="(item, index) in 6"
              width="18px"
              height="18px"
              class="garage-header__rating-star"
              alt="Ocena DobryMechanik.pl"
              :src="getStar(suggestedGarage?.garage?.ratingTotal || 0, index).toString()"
            />
          </div>
          <span class="garage-suggested__rating-total">{{ suggestedGarage?.garage?.ratingTotal }}</span>
          <span class="garage-suggested__total-comments">({{ suggestedGarage?.garage?.commentsTotal }} {{
      getCommentsTranslatePL(suggestedGarage?.garage?.commentsTotal) }})</span>
        </div>
      </div>
    </div>

    <div>
      <div class="garage-suggested__address">
        <NuxtImg
          src="/_nuxt/pin.svg"
          alt="Adres serwisu"
          width="20px"
          height="20px"
        />
        <p>
          <span>{{ suggestedGarage?.garage?.address }}, </span>
          <span v-if="suggestedGarage?.garage?.quarter"><u>{{ suggestedGarage.garage.quarter }}</u>, </span>
          <span><u>{{ suggestedGarage?.garage?.city }}</u></span>
        </p>
      </div>

      <div class="garage-suggested__popular">
        <NuxtImg
          src="/_nuxt/popularGarage.svg"
          alt="Popularność warsztatu"
          width="20px"
          height="20px"
        />
        <p>
          <span>
            {{ suggestedGarage?.garage?.visitsFromLast30Days }} {{ suggestedGarage?.garage?.visitsFromLast30Days === 1 ?
      'kierowca' : 'kierowców' }}
          </span>
          wybrało ten warsztat w ciągu ostatnich 30 dni
        </p>
      </div>
    </div>

    <div
      v-if="garageAdditional?.garageComments[0]"
      class="garage-suggested__comments"
    >
      <div class="garage-opinion">
        <div class="garage-opinion__header">
          <div class="garage-opinion__header-wrapper">
            <div class="garage-opinion__header-logo">{{ garageAdditional?.garageComments[0].name?.substring(0,
      1).toLocaleUpperCase() }}</div>
            <div>
              <div class="garage-opinion__author">{{ garageAdditional?.garageComments[0].name }}</div>
              <div class="garage-opinion__created-at">{{ dateFormat(garageAdditional?.garageComments[0]?.created) }}
              </div>
            </div>
          </div>

          <div class="garage-opinion__header-rating">
            <div class="garage-opinion__rating-stars">
              <NuxtImg
                v-for="(item, index) in 6"
                alt="Ocena DobryMechanik.pl"
                width="18px"
                height="18px"
                class="garage-opinion__rating-star"
                :src="getStar(garageAdditional?.garageComments[0].ratingTotal || 0, index).toString()"
              />
            </div>
          </div>
        </div>

        <div class="garage-opinion__description">
          <p>
            {{ opinionDescription(garageAdditional?.garageComments[0].description || '') }}
            <span
              class="garage-opinion__description-show-more"
              v-if="(garageAdditional?.garageComments[0].description?.length || 0) > shortDescriptionLength"
            >
              Rozwiń
            </span>
          </p>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<script
  setup
  lang="ts"
>
import { apiRequests } from '~/api/api-instance';
import type { RankingJsonldSuggestedGarageReadRankingRead } from '~/api/docs/api';
import { getStar } from '~/helpers/get-star';
import { dateFormat } from '~/helpers/dayjs';
import { getCommentsTranslatePL } from '@/helpers/helpers';

interface IProps {
  suggestedGarage?: RankingJsonldSuggestedGarageReadRankingRead;
}

// props
const props = defineProps<IProps>();

// use 
const route = useRoute();

// data
const shortDescriptionLength: number = 70;

// methods
const opinionDescription = (opinion: string) => {
  return opinion.length > shortDescriptionLength
    ? opinion.substring(0, shortDescriptionLength) + '...'
    : opinion;
};

const { data: garageAdditional, pending, error, refresh } = useAsyncData(
  `${props.suggestedGarage?.garage?.id?.toString()}`,
  async () => {
    const idString = props.suggestedGarage?.garage?.id?.toString();

    if (!idString) return;
    const GETgarageImageCollection = apiRequests.apiGarageGarageIdimagesGetCollection(idString, {
      'type[]': ['building-exterior-and-surroundings', 'example-cars', 'working-mechanics', 'workshop-hall']
    });
    const GETgarageComments = apiRequests.apiGarageGarageIdcommentsGetCollection(idString, { page: 1, itemsPerPage: 1, state: 1 });

    const [garageImages, garageComments] =
      await Promise.all([
        (await GETgarageImageCollection).data['hydra:member'],
        (await GETgarageComments).data['hydra:member']
      ]);

    return { garageImages, garageComments };
  }
);

</script>

<style
  lang="scss"
  scoped
>
@import url('assets/styles/components/garage-suggested.scss');
</style>